import { useMemo, useState } from 'react';
import type { FormEvent, JSX } from 'react';

import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import { Button } from '../Button/Button';

import styles from './StripeCardForm.module.css';

export interface CardSetupSecret {
  clientSecret: string;
  publishableKey: string;
  stripeAccountId?: string | null;
}

interface StripeCardFormProps {
  setup: CardSetupSecret;
  onSaved: () => void;
  onCancel: () => void;
}

/**
 * Saves a card with Stripe's own element, so the number never touches Revvly.
 * The card is only usable once Stripe confirms the setup and the connector books
 * it, so a caller should expect a short delay before it shows up as the default.
 */
export function StripeCardForm({ setup, onSaved, onCancel }: StripeCardFormProps): JSX.Element {
  const stripe = useMemo(
    () =>
      loadStripe(
        setup.publishableKey,
        setup.stripeAccountId ? { stripeAccount: setup.stripeAccountId } : undefined,
      ),
    [setup.publishableKey, setup.stripeAccountId],
  );
  return (
    <Elements stripe={stripe} options={{ clientSecret: setup.clientSecret }}>
      <Fields onSaved={onSaved} onCancel={onCancel} />
    </Elements>
  );
}

function Fields({
  onSaved,
  onCancel,
}: Pick<StripeCardFormProps, 'onSaved' | 'onCancel'>): JSX.Element {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    setError(null);
    const result = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });
    setSaving(false);
    if (result.error) {
      setError(result.error.message ?? 'The card could not be saved.');
      return;
    }
    onSaved();
  };

  return (
    <form className={styles.Form} onSubmit={submit}>
      <PaymentElement />
      {error && (
        <p className={styles.Error} role="alert">
          {error}
        </p>
      )}
      <div className={styles.Actions}>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" disabled={!stripe || saving}>
          {saving ? 'Saving' : 'Save card'}
        </Button>
      </div>
    </form>
  );
}
